import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MIN_SALES_DISPLAY_DURATION_SECONDS, type SalesDisplaySlideKind, type SalesDisplayWidgetKey } from "@/lib/sales-display-slides";
import { SalesDisplayDraftPreview } from "./SalesDisplayDraftPreview";
import {
  KIND_LABELS,
  WIDGET_OPTIONS,
  draftSlideFromForm,
  type FormState,
} from "./slide-form-state";

interface Props {
  form: FormState;
  onChange: (form: FormState) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function SalesDisplaySlideForm({
  form,
  onChange,
  file,
  onFileChange,
  onSave,
  onCancel,
  isSaving,
}: Props) {
  // Object-URL så previewen viser fila brukeren nettopp valgte, før den er
  // lastet opp. Den lever i appens origin, og utkast-iframen er samme origin,
  // så den kan leses der. Må frigis, ellers holder nettleseren på fila.
  const [localMediaUrl, setLocalMediaUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setLocalMediaUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setLocalMediaUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const draft = useMemo(
    () => draftSlideFromForm(form, localMediaUrl),
    [form, localMediaUrl],
  );
  const widgetLabel =
    form.kind === "widget"
      ? WIDGET_OPTIONS.find((option) => option.value === form.widgetKey)?.label ?? "Salgswidget"
      : null;

  return (
    <div className="grid gap-4 rounded-lg border p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
      <div className="grid content-start gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="slide-kind">Type</Label>
          <Select
            value={form.kind}
            onValueChange={(value: SalesDisplaySlideKind) => onChange({ ...form, kind: value })}
          >
            <SelectTrigger id="slide-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(KIND_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="slide-duration">Varighet i sekunder</Label>
          <Input
            id="slide-duration"
            type="number"
            min={MIN_SALES_DISPLAY_DURATION_SECONDS}
            value={form.durationSeconds}
            onChange={(event) => onChange({ ...form, durationSeconds: Number(event.target.value) })}
          />
        </div>

        {form.kind === "widget" ? (
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="slide-widget">Widget</Label>
            <Select
              value={form.widgetKey === "pipeline" ? "leaderboard" : form.widgetKey}
              onValueChange={(value: SalesDisplayWidgetKey) => {
                const label = WIDGET_OPTIONS.find((option) => option.value === value)?.label ?? "Salgswidget";
                onChange({ ...form, widgetKey: value, title: label });
              }}
            >
              <SelectTrigger id="slide-widget"><SelectValue /></SelectTrigger>
              <SelectContent>
                {WIDGET_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="slide-title">Tittel</Label>
              <Input
                id="slide-title"
                value={form.title}
                onChange={(event) => onChange({ ...form, title: event.target.value })}
                placeholder="Skriv en kort tittel"
              />
            </div>
            {form.kind === "message" && (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="slide-body">Beskjed</Label>
                <Textarea
                  id="slide-body"
                  value={form.body}
                  onChange={(event) => onChange({ ...form, body: event.target.value })}
                  rows={4}
                />
              </div>
            )}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="slide-media">
                {form.kind === "message" ? "Valgfri bilde- eller videobakgrunn" : "Mediefil"}
              </Label>
              <Input
                id="slide-media"
                type="file"
                accept={
                  form.kind === "image"
                    ? "image/jpeg,image/png,image/webp"
                    : form.kind === "video"
                      ? "video/mp4,video/webm"
                      : "image/jpeg,image/png,image/webp,video/mp4,video/webm"
                }
                onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
              />
              {form.mediaPath && !file && (
                <p className="text-xs text-muted-foreground">Eksisterende mediefil beholdes.</p>
              )}
            </div>
          </>
        )}

        <div className="flex items-center gap-3 md:col-span-2">
          <Switch
            id="slide-active"
            checked={form.isActive}
            onCheckedChange={(checked) => onChange({ ...form, isActive: checked })}
          />
          <Label htmlFor="slide-active">Aktiv i loopen</Label>
        </div>
        <div className="flex gap-2 md:col-span-2">
          <Button onClick={onSave} disabled={isSaving}>
            {form.id ? "Lagre endringer" : "Legg til slide"}
          </Button>
          {form.id && <Button variant="outline" onClick={onCancel}>Avbryt</Button>}
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Slik blir denne sliden</h3>
        <SalesDisplayDraftPreview draft={draft} widgetLabel={widgetLabel} />
      </div>
    </div>
  );
}
